import { Suspense, lazy, useState } from "react";
import { useTranslation } from "react-i18next";
import { WeeklyScheduleView } from "@/components/athlete/schedule/WeeklyScheduleView";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AthleteBrandHeader } from "@/components/athlete/AthleteBrandHeader";
import { AthleteHeroBackground } from "@/components/athlete/AthleteHeroBackground";
import { useThemeCustomization } from "@/hooks/useThemeCustomization";

// Lazy load the ReservationsView component to improve initial loading time
const ReservationsView = lazy(() =>
  import("@/components/athlete/schedule/ReservationsView").then(mod => ({
    default: mod.ReservationsView
  }))
);

// Create a loading fallback component
const LoadingFallback = () => (
  <Card className="w-full">
    <div className="p-6 space-y-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-72" />
      <div className="pt-4 space-y-2">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    </div>
  </Card>
);

const AthleteSchedule = () => {
  const { t } = useTranslation(["athlete", "schedule"]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const { athleteTheme, athleteBackgrounds, logoDark, brandName } = useThemeCustomization();
  const isFeatured = athleteTheme.variant === "dark-premium";
  
  return (
    <div className="athlete-page-bg min-h-screen flex flex-col w-full">
      <main className="flex-1 p-4 md:p-6 max-w-7xl mx-auto w-full">
        {isFeatured ? (
          <section className="athlete-schedule-hero relative -mx-4 -mt-4 overflow-hidden px-4 pb-4 pt-5 md:mx-0 md:mt-0 md:rounded-md md:px-6">
            <AthleteHeroBackground
              src={athleteBackgrounds.schedule}
              className="absolute inset-0 h-full w-full object-cover object-[center_22%]"
            />
            <div className="athlete-schedule-hero-overlay absolute inset-0" />
            <div className="relative z-10">
              <img src={logoDark} alt={brandName} className="athlete-brand-logo mb-3" />
              <h1 className="text-3xl font-bold text-foreground md:text-4xl">
                {t('tabs.schedule', { ns: 'athlete' })}
              </h1>
              <div className="mt-1.5 h-0.5 w-16 bg-primary" aria-hidden="true" />
              <p className="mt-1.5 max-w-[15rem] text-sm leading-snug text-foreground/90 md:max-w-xl md:text-base">
                {t('schedule.description', { ns: 'athlete' })}
              </p>
            </div>
          </section>
        ) : (
          <AthleteBrandHeader
            title={t('tabs.schedule', { ns: 'athlete' })}
            subtitle={t('schedule.description', { ns: 'athlete' })}
            backgroundKey="schedule"
          />
        )}

        <Suspense fallback={<LoadingFallback />}>
          <WeeklyScheduleView
            selectedDate={selectedDate}
            onDateSelect={setSelectedDate}
          />
        </Suspense>
      </main>
    </div>
  );
};

export default AthleteSchedule;
